/**
 * StepView lifecycle: the parent-facing emits, the loading overlay, the step
 * load, the retry reload and the completion action (moved verbatim from
 * StepView.vue).
 *
 * Registers, IN THIS ORDER, when called:
 *  1. watch isCompletedStep          → emit 'step:complete'
 *  2. watch isSkippedStep            → emit 'step:skippedFilter'
 *  3. watch repoUserStep.start_date  → emit 'step:start'
 *  4. watchEffect isLoading          → $q.loading.show()/hide()
 *  5. onBeforeMount                  → useStepData(stepDocId).getStep()
 *  6. onMounted                      → timerStop(); timerReset()
 * Call it BEFORE useStepDialogs (whose end-results watchEffect must run after
 * the step:complete emit watcher) and before useStepHeaderActions.
 */
import {
  computed,
  onBeforeMount,
  onMounted,
  watch,
  watchEffect,
  type ComputedRef,
  type Ref,
} from 'vue';
import { useQuasar } from 'quasar';
import type { RouteLocationNormalizedLoaded, Router } from 'vue-router';
import DOMPurify from 'dompurify';
import { useStepData } from '../useStepData';

type ReadRef<T> = Readonly<Ref<T>>;

export interface StepViewEmit {
  (
    e: 'step:complete',
    payload: { timeAccumulator: number; score: number },
  ): void;
  (e: 'step:start', dateTime: string): void;
  (e: 'action:completion'): void;
  (e: 'step:skippedFilter'): void;
}

type RouterLike = Pick<Router, 'push' | 'replace'>;
type RouteLike = Pick<RouteLocationNormalizedLoaded, 'name' | 'params' | 'query'>;

export interface UseStepViewLifecycleOptions {
  emit: StepViewEmit;
  stepDocId: string;
  userSectionDocId: string | undefined;
  router: RouterLike;
  route: RouteLike;
  isLoading: ReadRef<boolean>;
  isCompletedStep: ReadRef<boolean>;
  isSkippedStep: ReadRef<boolean>;
  score: ReadRef<number>;
  timeAccumulator: ReadRef<number>;
  repoUserStep: ReadRef<{ start_date?: string | null } | null | undefined>;
  documentStepRepo: ReadRef<{ instructions?: string | null } | null | undefined>;
  awaitSaves: () => Promise<unknown>;
  timerStop: () => void;
  timerReset: () => void;
}

export interface UseStepViewLifecycleReturn {
  retryLoadStep: () => Promise<void>;
  handleCompletionAction: () => void;
  completeActionButtonLabel: ComputedRef<'NEXT_STEP' | 'BACK_TO_STEP_LIST'>;
  sanitizedInstructions: ComputedRef<string>;
}

export function useStepViewLifecycle(
  opts: UseStepViewLifecycleOptions,
): UseStepViewLifecycleReturn {
  const { emit: emits, stepDocId, userSectionDocId, router, route } = opts;
  const { isLoading, isCompletedStep, isSkippedStep, score } = opts;
  const { timeAccumulator, repoUserStep, documentStepRepo } = opts;
  const $q = useQuasar();

  function handleCompletionAction() {
    if (!userSectionDocId) {
      void router.push({ name: 'steps' });
    } else {
      emits('action:completion');
    }
  }

  async function retryLoadStep() {
    // A reload drops anything not yet saved: let pending saves land (capped).
    await opts.awaitSaves();
    // Force reload by navigating to self
    await router.replace({
      name: route.name as string,
      params: route.params,
      query: { ...route.query, retry: Date.now().toString() },
    });
    // Trigger data refetch
    window.location.reload();
  }

  const completeActionButtonLabel = computed(() => {
    return userSectionDocId ? 'NEXT_STEP' : 'BACK_TO_STEP_LIST';
  });

  watch(
    () => isCompletedStep.value,
    (val) => {
      if (val)
        emits('step:complete', {
          score: score?.value,
          timeAccumulator: timeAccumulator.value,
        });
    },
  );

  watch(
    () => isSkippedStep.value,
    (val) => {
      if (val) emits('step:skippedFilter');
    },
  );

  watch(
    () => repoUserStep.value?.start_date,
    (val) => {
      if (!val) return;
      emits('step:start', val);
    },
  );

  watchEffect(() => {
    if (isLoading.value) $q.loading.show();
    else $q.loading.hide();
  });

  const sanitizedInstructions = computed(() => {
    return documentStepRepo.value?.instructions
      ? DOMPurify.sanitize(documentStepRepo.value.instructions)
      : '';
  });

  onBeforeMount(async () => {
    const { getStep } = useStepData(stepDocId);
    await getStep();
  });

  onMounted(() => {
    opts.timerStop();
    opts.timerReset();
  });

  return {
    retryLoadStep,
    handleCompletionAction,
    completeActionButtonLabel,
    sanitizedInstructions,
  };
}
